import React, { useMemo, useState } from "react";
import { Box, Button, Flex, Heading, Icon, Table } from "@telia/teddy";
import { type FeedbackEntry } from "./feedbackTypes";
import "./ReviewTrends.less";

type DealerTrend = {
    dealerName: string;
    dealerCode: string;
    reviewCount: number;
    avgScore: number;
};

type AgentTrend = {
    agentId?: string;
    agentName: string;
    dealerName: string;
    dealerCode: string;
    reviewCount: number;
    avgScore: number;
};

type SortDir = "asc" | "desc";

function sortRows<T extends Record<string, string | number>>(rows: T[], field: keyof T, dir: SortDir): T[] {
    return [...rows].sort((a, b) => {
        const aVal = a[field];
        const bVal = b[field];
        if (typeof aVal === "number" && typeof bVal === "number") {
            return dir === "asc" ? aVal - bVal : bVal - aVal;
        }
        const aStr = String(aVal);
        const bStr = String(bVal);
        return dir === "asc" ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
    });
}

const PAGE_SIZE = 10;

type AriaSort = "ascending" | "descending" | "none";
function getAriaSort(isActive: boolean, dir: SortDir): AriaSort {
    if (!isActive) return "none";
    return dir === "asc" ? "ascending" : "descending";
}

type DealerTableProps = {
    rows: DealerTrend[];
    sort: { field: keyof DealerTrend; dir: SortDir };
    onSort: (field: keyof DealerTrend) => void;
};

const DealerTable: React.FC<DealerTableProps> = ({ rows, sort, onSort }) => (
    <Table>
        <Table.Header>
            <Table.Row>
                <Table.Head
                    className="review-trends__sortable-head"
                    aria-sort={getAriaSort(sort.field === "dealerName", sort.dir)}
                    onClick={() => onSort("dealerName")}
                >
                    Forhandler <SortIcon active={sort.field === "dealerName"} dir={sort.dir} />
                </Table.Head>
                <Table.Head
                    className="review-trends__sortable-head"
                    aria-sort={getAriaSort(sort.field === "reviewCount", sort.dir)}
                    onClick={() => onSort("reviewCount")}
                    numeric
                >
                    # <SortIcon active={sort.field === "reviewCount"} dir={sort.dir} />
                </Table.Head>
                <Table.Head
                    className="review-trends__sortable-head"
                    aria-sort={getAriaSort(sort.field === "avgScore", sort.dir)}
                    onClick={() => onSort("avgScore")}
                    numeric
                >
                    Avg. <SortIcon active={sort.field === "avgScore"} dir={sort.dir} />
                </Table.Head>
            </Table.Row>
        </Table.Header>
        <Table.Body>
            {rows.length === 0 ? (
                <Table.Row>
                    <Table.Cell colSpan={3}>Ingen data</Table.Cell>
                </Table.Row>
            ) : (
                rows.map((row) => (
                    <Table.Row key={`${row.dealerCode}-${row.dealerName}`}>
                        <Table.Cell>
                            {row.dealerName || "–"}
                            {row.dealerCode && <span className="review-trends__secondary"> ({row.dealerCode})</span>}
                        </Table.Cell>
                        <Table.Cell numeric>{row.reviewCount}</Table.Cell>
                        <Table.Cell numeric>{row.avgScore}</Table.Cell>
                    </Table.Row>
                ))
            )}
        </Table.Body>
    </Table>
);

type AgentTableProps = {
    rows: AgentTrend[];
    sort: { field: keyof AgentTrend; dir: SortDir };
    onSort: (field: keyof AgentTrend) => void;
};

const AgentTable: React.FC<AgentTableProps> = ({ rows, sort, onSort }) => (
    <Table>
        <Table.Header>
            <Table.Row>
                <Table.Head
                    className="review-trends__sortable-head"
                    aria-sort={getAriaSort(sort.field === "agentName", sort.dir)}
                    onClick={() => onSort("agentName")}
                >
                    Navn <SortIcon active={sort.field === "agentName"} dir={sort.dir} />
                </Table.Head>
                <Table.Head
                    className="review-trends__sortable-head"
                    aria-sort={getAriaSort(sort.field === "dealerName", sort.dir)}
                    onClick={() => onSort("dealerName")}
                >
                    Forhandler <SortIcon active={sort.field === "dealerName"} dir={sort.dir} />
                </Table.Head>
                <Table.Head
                    className="review-trends__sortable-head"
                    aria-sort={getAriaSort(sort.field === "reviewCount", sort.dir)}
                    onClick={() => onSort("reviewCount")}
                    numeric
                >
                    # <SortIcon active={sort.field === "reviewCount"} dir={sort.dir} />
                </Table.Head>
                <Table.Head
                    className="review-trends__sortable-head"
                    aria-sort={getAriaSort(sort.field === "avgScore", sort.dir)}
                    onClick={() => onSort("avgScore")}
                    numeric
                >
                    Avg. <SortIcon active={sort.field === "avgScore"} dir={sort.dir} />
                </Table.Head>
            </Table.Row>
        </Table.Header>
        <Table.Body>
            {rows.length === 0 ? (
                <Table.Row>
                    <Table.Cell colSpan={4}>Ingen data</Table.Cell>
                </Table.Row>
            ) : (
                rows.map((row) => (
                    <Table.Row key={`${row.agentId || row.agentName}|${row.dealerCode || row.dealerName}`}>
                        <Table.Cell>{row.agentName || "–"}</Table.Cell>
                        <Table.Cell>
                            {row.dealerName || "–"}
                            {row.dealerCode && <span className="review-trends__secondary"> ({row.dealerCode})</span>}
                        </Table.Cell>
                        <Table.Cell numeric>{row.reviewCount}</Table.Cell>
                        <Table.Cell numeric>{row.avgScore}</Table.Cell>
                    </Table.Row>
                ))
            )}
        </Table.Body>
    </Table>
);

type Props = {
    feedback: FeedbackEntry[];
};

function getSortIconName(active: boolean, dir: SortDir): "arrow-up" | "arrow-down" | "sorter" {
    if (!active) return "sorter";
    return dir === "asc" ? "arrow-up" : "arrow-down";
}

type SortIconProps = { active: boolean; dir: SortDir };
const SortIcon: React.FC<SortIconProps> = ({ active, dir }) => (
    <Icon
        name={getSortIconName(active, dir)}
        size="xs"
        className={`review-trends__sort-icon${active ? " review-trends__sort-icon--active" : ""}`}
    />
);

const ReviewTrends: React.FC<Props> = ({ feedback }) => {
    const reviews = useMemo(
        () =>
            feedback.filter(
                (e): e is FeedbackEntry & { score: number } => e.feedbackType === "review" && e.score !== null,
            ),
        [feedback],
    );

    const dealerRows = useMemo((): DealerTrend[] => {
        const map = new Map<string, { sum: number; count: number; name: string }>();
        for (const e of reviews) {
            const key = e.dealerCode ?? "";
            const existing = map.get(key);
            if (existing) {
                existing.sum += e.score;
                existing.count += 1;
            } else {
                map.set(key, { sum: e.score, count: 1, name: e.dealerName ?? "" });
            }
        }
        return Array.from(map.entries()).map(([dealerCode, { sum, count, name }]) => ({
            dealerCode,
            dealerName: name,
            reviewCount: count,
            avgScore: Math.round((sum / count) * 10) / 10,
        }));
    }, [reviews]);

    const agentRows = useMemo((): AgentTrend[] => {
        const map = new Map<string, { sum: number; count: number; dealerName: string; dealerCode: string }>();
        for (const e of reviews) {
            const key = `${e.agentName ?? ""}|${e.dealerName ?? ""}`;
            const existing = map.get(key);
            if (existing) {
                existing.sum += e.score;
                existing.count += 1;
            } else {
                map.set(key, {
                    sum: e.score,
                    count: 1,
                    dealerName: e.dealerName ?? "",
                    dealerCode: e.dealerCode ?? "",
                });
            }
        }
        return Array.from(map.entries()).map(([key, { sum, count, dealerName, dealerCode }]) => ({
            agentName: key.split("|")[0],
            dealerName,
            dealerCode,
            reviewCount: count,
            avgScore: Math.round((sum / count) * 10) / 10,
        }));
    }, [reviews]);

    const [dealerSort, setDealerSort] = useState<{ field: keyof DealerTrend; dir: SortDir }>({
        field: "reviewCount",
        dir: "desc",
    });
    const [agentSort, setAgentSort] = useState<{ field: keyof AgentTrend; dir: SortDir }>({
        field: "reviewCount",
        dir: "desc",
    });

    const sortedDealers = useMemo(
        () => sortRows(dealerRows, dealerSort.field, dealerSort.dir),
        [dealerRows, dealerSort],
    );
    const sortedAgents = useMemo(() => sortRows(agentRows, agentSort.field, agentSort.dir), [agentRows, agentSort]);

    const toggleDealerSort = (field: keyof DealerTrend) => {
        setDealerSort((prev) =>
            prev.field === field ? { field, dir: prev.dir === "asc" ? "desc" : "asc" } : { field, dir: "desc" },
        );
    };

    const toggleAgentSort = (field: keyof AgentTrend) => {
        setAgentSort((prev) =>
            prev.field === field ? { field, dir: prev.dir === "asc" ? "desc" : "asc" } : { field, dir: "desc" },
        );
    };

    const [showAllDealers, setShowAllDealers] = useState(false);
    const [showAllAgents, setShowAllAgents] = useState(false);

    const visibleDealers = showAllDealers ? sortedDealers : sortedDealers.slice(0, PAGE_SIZE);
    const visibleAgents = showAllAgents ? sortedAgents : sortedAgents.slice(0, PAGE_SIZE);

    return (
        <Box className="review-trends">
            <Heading mb="100" as="h3" variant="title-200" className="review-trends__heading">
                Trender
            </Heading>
            <Flex direction="row" gap="400" align="start" wrap="wrap">
                <Box className="review-trends__table-wrap">
                    <Heading mb="100" as="h4" variant="title-100" className="review-trends__table-heading">
                        Per forhandler
                    </Heading>
                    <DealerTable rows={visibleDealers} sort={dealerSort} onSort={toggleDealerSort} />
                    {sortedDealers.length > PAGE_SIZE && (
                        <Button
                            variant="text"
                            size="sm"
                            className="review-trends__expand-btn"
                            onClick={() => setShowAllDealers((prev) => !prev)}
                        >
                            {showAllDealers ? "Vis færre" : `Vis alle (${sortedDealers.length})`}
                        </Button>
                    )}
                </Box>
                <Box className="review-trends__table-wrap">
                    <Heading mb="100" as="h4" variant="title-100" className="review-trends__table-heading">
                        Per agent
                    </Heading>
                    <AgentTable rows={visibleAgents} sort={agentSort} onSort={toggleAgentSort} />
                    {sortedAgents.length > PAGE_SIZE && (
                        <Button
                            variant="text"
                            size="sm"
                            className="review-trends__expand-btn"
                            onClick={() => setShowAllAgents((prev) => !prev)}
                        >
                            {showAllAgents ? "Vis færre" : `Vis alle (${sortedAgents.length})`}
                        </Button>
                    )}
                </Box>
            </Flex>
        </Box>
    );
};

export default ReviewTrends;
